import { type ColumnDef } from '@tanstack/react-table';
import { ArrowDownToLineIcon, ArrowUpFromLineIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  OpsOrderHealthEnum,
  ReturnOrderDiscrepancyStatus,
  type OpsOrder,
} from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';

import { OrderColumnHeader } from './ColumnHeader';
import { OrderRowActions } from './UnifiedRowActions';
import {
  selectColumn,
  statusColumn,
  dateColumn,
  warehouseColumn,
  actionsColumn,
} from './sharedColumns';
import { getOrderKind } from '../details/orderFormPolicy';

export const columns: ColumnDef<OpsOrder>[] = [
  selectColumn(),
  statusColumn(),
  {
    id: 'direction',
    enableSorting: false,
    meta: {
      get title() {
        return i18n.t('Order:columnDirection');
      },
    },
    header: () => null,
    size: 40,
    cell: ({ row }) => {
      const type = row.original.type;
      const isInbound =
        getOrderKind(type, row.original.status).orderKind === 'inbound';
      const label = isInbound
        ? i18n.t('Order:directionInbound')
        : i18n.t('Order:directionOutbound');
      const Icon = isInbound ? ArrowDownToLineIcon : ArrowUpFromLineIcon;
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Icon className="tw:mx-auto tw:size-4 tw:text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent side="right">
            {label} ({type})
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    id: 'partner',
    meta: {
      get title() {
        return i18n.t('Order:columnPartner');
      },
    },
    header: ({ column }) => (
      <OrderColumnHeader
        column={column}
        title={i18n.t('Order:columnPartner')}
      />
    ),
    accessorFn: (row) => row.store?.name ?? '',
    cell: ({ row }) => {
      const name = row.original.store?.name || '-';
      const subtitle = [row.original.city, row.original.country]
        .filter(Boolean)
        .join(', ');

      return (
        <div className="tw:flex tw:flex-col">
          <span className="tw:font-medium tw:truncate">{name}</span>
          {subtitle && (
            <span className="tw:text-muted-foreground tw:text-xs tw:truncate">
              {subtitle}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'containers',
    meta: {
      get title() {
        return i18n.t('Order:columnAmount');
      },
    },
    header: ({ column }) => (
      <OrderColumnHeader column={column} title={i18n.t('Order:columnAmount')} />
    ),
    accessorFn: (row) => row.total_amount ?? 0,
    cell: ({ row }) => {
      const isInbound =
        getOrderKind(row.original.type, row.original.status).orderKind ===
        'inbound';
      if (isInbound) {
        return (
          <div className="tw:flex tw:justify-center">
            <span className="tw:text-muted-foreground tw:text-xs tw:truncate">
              N/A
            </span>
          </div>
        );
      }
      return (
        <div className="tw:flex tw:justify-center">
          <Badge
            variant="outline"
            className="tw:justify-center tw:tabular-nums"
          >
            {row.original.total_amount}
          </Badge>
        </div>
      );
    },
    enableSorting: false,
  },
  {
    ...dateColumn({
      id: 'createdAt',
      titleKey: 'Order:columnCreatedAt',
      accessorKey: 'created_at',
      showTime: true,
    }),
    cell: ({ row }) => {
      const date = new Date(row.original.created_at);
      const dateString = date.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const timeString = date.toLocaleTimeString(i18n.language, {
        hour: '2-digit',
        minute: '2-digit',
      });
      const formatted = `${dateString} ${timeString}`;
      return (
        <div className="tw:flex tw:flex-col">
          <span>{formatted}</span>
          {row.original.created_by && (
            <span className="tw:text-muted-foreground tw:text-xs tw:truncate">
              {row.original.created_by?.name || '-'}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'health',
    accessorKey: 'health',
    meta: {
      get title() {
        return i18n.t('Order:columnHealth');
      },
    },
    header: ({ column }) => (
      <OrderColumnHeader column={column} title={i18n.t('Order:columnHealth')} />
    ),
    cell: ({ row }) => {
      const health = row.original.health;
      if (health === OpsOrderHealthEnum.OnTime) {
        return (
          <Badge variant="secondary">{i18n.t('Order:healthOnTime')}</Badge>
        );
      }
      return (
        <Badge variant="destructive">{i18n.t('Order:healthAtRisk')}</Badge>
      );
    },
  },
  {
    id: 'discrepancy',
    accessorKey: 'discrepancy_status',
    enableSorting: false,
    meta: {
      get title() {
        return i18n.t('Order:columnDiscrepancy');
      },
    },
    header: () => i18n.t('Order:columnDiscrepancy'),
    cell: ({ row }) => {
      switch (row.original.discrepancy_status) {
        case ReturnOrderDiscrepancyStatus.Open:
          return (
            <Badge variant="warning">
              {i18n.t('Order:discrepancyOpenBadge')}
            </Badge>
          );
        case ReturnOrderDiscrepancyStatus.Resolved:
          return (
            <Badge variant="secondary">
              {i18n.t('Order:discrepancyResolved')}
            </Badge>
          );
        default:
          return null;
      }
    },
  },
  {
    id: 'country',
    accessorKey: 'country',
    meta: {
      get title() {
        return i18n.t('Order:columnCountry');
      },
    },
    header: () => i18n.t('Order:columnCountry'),
  },
  {
    id: 'carrier',
    accessorKey: 'carrier',
    meta: {
      get title() {
        return i18n.t('Order:columnCarrier');
      },
    },
    header: () => i18n.t('Order:columnCarrier'),
    cell: ({ row }) => row.original.carrier || '-',
  },
  dateColumn({
    id: 'lastUpdated',
    titleKey: 'Order:columnLastUpdated',
    accessorKey: 'last_updated',
  }),
  {
    id: 'createdBy',
    accessorKey: 'created_by',
    meta: {
      get title() {
        return i18n.t('Order:columnCreatedBy');
      },
    },
    header: () => i18n.t('Order:columnCreatedBy'),
    accessorFn: (row) => row.created_by?.name ?? '-',
    enableSorting: true,
  },
  warehouseColumn(),
  actionsColumn(OrderRowActions),
];

export const defaultHiddenColumns: Record<string, boolean> = {
  country: false,
  carrier: false,
  createdAt: false,
  warehouse: false,
  discrepancy: false,
};
